'use client';

import { useState, useEffect } from 'react';
import { Avatar, Menu, MenuItem, Typography, IconButton, Box } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '@/contexts/AuthContext';
import { usePoints } from '@/contexts/PointsContext';
import Link from 'next/link';

export default function UserMenu() {
  const { user, logout } = useAuth();
  const { balance } = usePoints();
  const [mounted, setMounted] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
  };

  // Generate avatar text from email (first letter of the email)
  const avatarText = user?.email ? user.email[0].toUpperCase() : 'U';

  if (!mounted) {
    return null;
  }

  return (
    <>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        cursor: 'pointer',
        '&:hover': { opacity: 0.8 }
      }}>
        <Box
          onClick={handleClick}
          sx={{
            alignItems: 'center',
            gap: 1,
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
            {avatarText}
          </Avatar>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body2" color="text.primary">
              {user?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {balance.toLocaleString()} Points
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ display: { xs: 'flex', sm: 'none' } }}
          aria-controls={open ? 'user-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <MenuIcon />
        </IconButton>
      </Box>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem disabled>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
        </MenuItem>
        <MenuItem component={Link} href="/surveys">
          Surveys
        </MenuItem>
        <MenuItem component={Link} href="/offerwalls">
          Offerwalls
        </MenuItem>
        <MenuItem component={Link} href="/rewards">
          Rewards
        </MenuItem>
        <MenuItem component={Link} href="/referrals">
          Referrals
        </MenuItem>
        <MenuItem component={Link} href="/settings">
          Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          Logout
        </MenuItem>
      </Menu>
    </>
  );
}